import axios from 'axios';
import { resetSession } from 'src/redux/modalSlice';
import { USER_ROLES } from 'src/utils/constants';
import API_URL from './apiConstants';

let appStore;

export const injectStore = (_store) => {
  appStore = _store;
};

export const BASEAPI = axios.create({
  baseURL: API_URL.baseUrl,
  timeout: 20000,
});

export const setAPIAuthHeader = (token) => {
  // Add auth token for all the upcoming API calls
  BASEAPI.defaults.headers.common.Authorization = `Token ${token}`;
};

BASEAPI.interceptors.response.use((response) => response,
  (error) => {
    const errorObj = error.response || error.request || error;
    console.log('global error', errorObj);
    const isFromLoginApi = errorObj?.config?.url === API_URL.login || errorObj?.config?.url?.includes('sign-in');
    if (error.response?.status === 401 && !isFromLoginApi) {
      appStore.dispatch(resetSession({ isSessionTimedOut: true }));
    }
    return Promise.reject(error);
  });

export const getAPIError = (errorObj) => {
  if (errorObj.response) return errorObj.response;
  if (errorObj.request) return errorObj.request;
  return errorObj;
};

const fetchData = (method = 'GET', url, data = null, config = {}) => BASEAPI({
  method,
  url,
  data,
  ...config,
}).then((response) => response.data)
  .catch((error) => getAPIError(error));

export default fetchData;

const getLoginUrl = (role) => {
  let loginUrl = API_URL.login;
  if (role === USER_ROLES.approver) {
    loginUrl = API_URL.approverLogin;
  } else if (role === USER_ROLES.support) {
    loginUrl = API_URL.supportLogin;
  } else if (role === USER_ROLES.siteManager) {
    loginUrl = API_URL.siteManagerLogin;
  } else if (role === USER_ROLES.operator) {
    loginUrl = API_URL.operatorLogin;
  }
  return loginUrl;
};

// Generate API url with params
const generateAPIUrlWithParams = (baseUrl, limit = 10, pageNo = 1, queryParams = {}) => {
  let URL = `${baseUrl}?page=${pageNo}&page_size=${limit}`;
  const params = Object.keys(queryParams);
  params.forEach((key) => {
    if (queryParams[key]) {
      URL += `&${key}=${queryParams[key]}`;
    }
  });
  return URL;
};

const generateAPIUrlWithQueryParams = (baseUrl, queryParams = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(queryParams).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      searchParams.append(key, value);
    }
  });
  const queryString = searchParams.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};

// auth
const userLogin = (data) => fetchData('POST', getLoginUrl(data?.role), data);
const userLogout = (data) => fetchData('POST', API_URL.logout, data);
const resetPassword = (data) => fetchData('POST', API_URL.resetPassword, data);
const getTableColumnPreference = (tableKey) => fetchData(
  'GET',
  API_URL.tableColumnPreference.replace('{tableKey}', tableKey)
);
const updateTableColumnPreference = (tableKey, data) => fetchData(
  'PATCH',
  API_URL.tableColumnPreference.replace('{tableKey}', tableKey),
  data
);
// support portal
const getSupportMe = () => fetchData('GET', API_URL.supportMe);
const getSupportLookups = (companyId) => fetchData('GET', API_URL.supportLookups(companyId));
const getSupportSummary = (companyId, params) => fetchData(
  'GET',
  generateAPIUrlWithQueryParams(API_URL.supportSummary(companyId), params)
);
const createSupportSite = (companyId, data) => fetchData('POST', API_URL.supportSites(companyId), data);
const getSupportSiteDetails = (companyId, siteId) => fetchData(
  'GET', API_URL.supportSiteDetails(companyId, siteId)
);
const updateSupportSite = (companyId, siteId, data) => fetchData(
  'PATCH', API_URL.supportSiteDetails(companyId, siteId), data
);
const getSupportList = (baseUrl, limit, pageNo, params) => fetchData(
  'GET',
  generateAPIUrlWithParams(baseUrl, limit, pageNo, params)
);
const getSupportSites = (companyId, limit, pageNo, params) => getSupportList(
  API_URL.supportSites(companyId), limit, pageNo, params
);
const getSupportIssueTransactions = (companyId, limit, pageNo, params) => getSupportList(
  API_URL.supportIssueTransactions(companyId),
  limit,
  pageNo,
  params
);
const exportSupportIssueTransactions = (companyId, params = {}) => BASEAPI.get(
  API_URL.supportIssueTransactionExport(companyId), { params, responseType: 'blob' }
);
const updateSupportIssueTransaction = (companyId, transactionId, data) => fetchData(
  'PATCH', API_URL.supportIssueTransactionDetails(companyId, transactionId), data
);
const getSupportInwardTransactions = (companyId, limit, pageNo, params) => getSupportList(
  API_URL.supportInwardTransactions(companyId),
  limit,
  pageNo,
  params
);
const createSupportInwardTransaction = (companyId, data) => fetchData(
  'POST',
  API_URL.supportInwardTransactions(companyId),
  data
);
const updateSupportInwardTransaction = (companyId, transactionId, data) => fetchData(
  'PATCH',
  API_URL.supportInwardTransactionDetails(companyId, transactionId),
  data
);
const exportSupportInwardTransactions = (companyId, params = {}) => BASEAPI.get(
  API_URL.supportInwardTransactionExport(companyId), { params, responseType: 'blob' }
);
const getSupportTransferTransactions = (companyId, limit, pageNo, params) => getSupportList(
  API_URL.supportTransferTransactions(companyId),
  limit,
  pageNo,
  params
);
const exportSupportTransferTransactions = (companyId, params = {}) => BASEAPI.get(
  API_URL.supportTransferTransactionExport(companyId), { params, responseType: 'blob' }
);
const getSupportExternalTransactions = (companyId, limit, pageNo, params) => getSupportList(
  API_URL.supportExternalTransactions(companyId),
  limit,
  pageNo,
  params
);
const exportSupportExternalTransactions = (companyId, params = {}) => BASEAPI.get(
  API_URL.supportExternalTransactionExport(companyId), { params, responseType: 'blob' }
);
const getSupportVehicles = (companyId, limit, pageNo, params) => getSupportList(
  API_URL.supportVehicles(companyId),
  limit,
  pageNo,
  params
);
const exportSupportVehicles = (companyId, params = {}) => BASEAPI.get(
  API_URL.supportVehicleExport(companyId), { params, responseType: 'blob' }
);
const createSupportVehicle = (companyId, data) => fetchData('POST', API_URL.supportVehicles(companyId), data);
const getSupportVehicleDetails = (companyId, vehicleId) => fetchData(
  'GET',
  API_URL.supportVehicleDetails(companyId, vehicleId)
);
const updateSupportVehicle = (companyId, vehicleId, data) => fetchData(
  'PATCH',
  API_URL.supportVehicleDetails(companyId, vehicleId),
  data
);
const getSupportTanks = (companyId, limit, pageNo, params) => getSupportList(
  API_URL.supportTanks(companyId),
  limit,
  pageNo,
  params
);
const createSupportTank = (companyId, data) => fetchData('POST', API_URL.supportTanks(companyId), data);
const getSupportTankDetails = (companyId, tankId) => fetchData(
  'GET',
  API_URL.supportTankDetails(companyId, tankId)
);
const updateSupportTank = (companyId, tankId, data) => fetchData(
  'PATCH',
  API_URL.supportTankDetails(companyId, tankId),
  data
);
const getSupportDispensers = (companyId, limit, pageNo, params) => getSupportList(
  API_URL.supportDispensers(companyId),
  limit,
  pageNo,
  params
);
const createSupportDispenser = (companyId, data) => fetchData('POST', API_URL.supportDispensers(companyId), data);
const getSupportDispenserDetails = (companyId, dispenserId) => fetchData(
  'GET',
  API_URL.supportDispenserDetails(companyId, dispenserId)
);
const updateSupportDispenser = (companyId, dispenserId, data) => fetchData(
  'PATCH',
  API_URL.supportDispenserDetails(companyId, dispenserId),
  data
);
const getSupportDispenserLinkedCards = (companyId, dispenserId, limit, pageNo, params) => getSupportList(
  API_URL.supportDispenserLinkedCards(companyId, dispenserId), limit, pageNo, params
);
const resetSupportDispenserCards = (companyId, dispenserId) => fetchData(
  'PUT', API_URL.supportDispenserResetCards(companyId, dispenserId)
);
const mapSupportCardsToDispenser = (companyId, data) => fetchData(
  'POST', API_URL.supportMapCardsToDispenser(companyId), data
);
const getSupportCards = (companyId, limit, pageNo, params) => getSupportList(
  API_URL.supportCards(companyId),
  limit,
  pageNo,
  params
);
const createSupportCard = (companyId, data) => fetchData('POST', API_URL.supportCards(companyId), data);
const getSupportCardDetails = (companyId, cardId) => fetchData(
  'GET',
  API_URL.supportCardDetails(companyId, cardId)
);
const updateSupportCard = (companyId, cardId, data) => fetchData(
  'PATCH',
  API_URL.supportCardDetails(companyId, cardId),
  data
);
const exportSupportCards = (companyId, params = {}) => BASEAPI.get(
  API_URL.supportCardExport(companyId),
  { params, responseType: 'blob' }
);
const removeSupportCardDispenser = (companyId, mapperId) => fetchData(
  'DELETE', API_URL.supportRemoveCardDispenser(companyId, mapperId)
);
const getSupportMissedTransactions = (companyId, limit, pageNo, params) => getSupportList(
  API_URL.supportMissedTransactions(companyId),
  limit,
  pageNo,
  params
);
// sites
const createSite = (data) => fetchData('POST', API_URL.createSite, data);
const getSiteList = (limit, pageNo, params) => fetchData('GET', generateAPIUrlWithParams(API_URL.siteList, limit, pageNo, params));
const getSiteDetails = (siteId) => fetchData('GET', API_URL.siteDetails.replace('{siteId}', siteId));
const updateSiteDetails = (siteId, data) => fetchData('PUT', API_URL.updateSite.replace('{siteId}', siteId), data);
// requestors
const createRequestor = (data) => fetchData('POST', API_URL.createRequestor, data);
const getRequestorList = (limit, pageNo, params) => fetchData('GET', generateAPIUrlWithParams(API_URL.requestorList, limit, pageNo, params));
const getRequestorDetails = (userId) => fetchData('GET', API_URL.requestorDetails.replace('{userId}', userId));
const updateRequestorDetails = (userId, data) => fetchData('PUT', API_URL.updateRequestor.replace('{userId}', userId), data);
// vehicles
const createVehicle = (data) => fetchData('POST', API_URL.createVehicle, data);
const getVehicleList = (limit, pageNo, params) => fetchData('GET', generateAPIUrlWithParams(API_URL.vehicleList, limit, pageNo, params));
const getVehicleDetails = (vehicleId) => fetchData('GET', API_URL.vehicleDetails.replace('{vehicleId}', vehicleId));
const updateVehicleDetails = (vehicleId, data) => fetchData('PUT', API_URL.updateVehicle.replace('{vehicleId}', vehicleId), data);
// groups
const createGroup = (groupName, data) => fetchData('POST', API_URL.createGroup.replace('{groupName}', groupName), data);
const getGroupList = (groupName, params, limit = 100, pageNo) => {
  const URL = API_URL.groupList.replace('{groupName}', groupName);
  return fetchData('GET', generateAPIUrlWithParams(URL, limit, pageNo, params));
};
const updateGroupDetails = (groupName, groupId, data) => fetchData('PUT', API_URL.getGroupUrl(groupName, groupId), data);
const deleteGroup = (groupName, groupId) => fetchData('DELETE', API_URL.getGroupUrl(groupName, groupId));
// contractors
const createContractor = (data) => fetchData('POST', API_URL.createContractor, data);
const getContractorList = (params, limit = 100, pageNo) => fetchData('GET', generateAPIUrlWithParams(API_URL.contractorList, limit, pageNo, params));
const getContractorDetails = (contractorId) => fetchData('GET', API_URL.contractorDetails.replace('{contractorId}', contractorId));
const updateContractorDetails = (contractorId, data) => fetchData('PUT', API_URL.updateContractor.replace('{contractorId}', contractorId), data);
const patchContractorDetails = (contractorId, data) => fetchData('PATCH', API_URL.patchContractor.replace('{contractorId}', contractorId), data);
const deleteContractor = (contractorId) => fetchData('DELETE', API_URL.deleteContractor.replace('{contractorId}', contractorId));
// operators
const createOperator = (data) => fetchData('POST', API_URL.createOperator, data);
const getOperatorList = (limit, pageNo, params) => fetchData('GET', generateAPIUrlWithParams(API_URL.operatorList, limit, pageNo, params));
const getOperatorDetails = (userId) => fetchData('GET', API_URL.operatorDetails.replace('{userId}', userId));
const updateOperatorDetails = (userId, data) => fetchData('PUT', API_URL.updateOperator.replace('{userId}', userId), data);
// tanks
const createTank = (data) => fetchData('POST', API_URL.createTank, data);
const getTankList = (limit, pageNo, params) => fetchData('GET', generateAPIUrlWithParams(API_URL.tankList, limit, pageNo, params));
const getTankDetails = (tankId) => fetchData('GET', API_URL.tankDetails.replace('{tankId}', tankId));
const getTankFuelGraph = (tankId, data) => fetchData('POST', API_URL.tankFuelGraph.replace('{tankId}', tankId), data);
const getTankFuelEvents = (tankId, params) => fetchData(
  'GET',
  generateAPIUrlWithQueryParams(API_URL.tankFuelEvents.replace('{tankId}', tankId), params)
);
const updateTankDetails = (tankId, data) => fetchData('PUT', API_URL.updateTank.replace('{tankId}', tankId), data);
const getDispenserLinkedCards = (dispenserId, limit = 10, pageNo = 1, params = {}) => {
  const url = generateAPIUrlWithParams(
    API_URL.mappedCardsToDispenser.replace('{dispenserId}', dispenserId),
    limit,
    pageNo,
    params
  );
  return fetchData('GET', url);
};
// dispensers
const createDispenser = (data) => fetchData('POST', API_URL.createDispenser, data);
const getDispenserList = (limit, pageNo, params) => fetchData('GET', generateAPIUrlWithParams(API_URL.dispenserList, limit, pageNo, params));
const getDispenserDetails = (dispenserId) => fetchData('GET', API_URL.dispenserDetails.replace('{dispenserId}', dispenserId));
const updateDispenserDetails = (dispenserId, data) => fetchData('PUT', API_URL.updateDispenser.replace('{dispenserId}', dispenserId), data);
const resetDispenserCards = (dispenserId) => fetchData('PUT', API_URL.resetDispenserCards.replace('{dispenserId}', dispenserId));
const mapCardsToDispenser = (data) => fetchData('POST', API_URL.mapCardsToDispenser, data);
// cards
const createCard = (data) => fetchData('POST', API_URL.createCard, data);
const getCardList = (limit, pageNo, params) => fetchData('GET', generateAPIUrlWithParams(API_URL.cardList, limit, pageNo, params));
const getCardDetails = (cardId) => fetchData('GET', API_URL.cardDetails.replace('{cardId}', cardId));
const updateCardDetails = (cardId, data) => fetchData('PUT', API_URL.updateCard.replace('{cardId}', cardId), data);
// vehicle driver
const createDriver = (data) => fetchData('POST', API_URL.createDriver, data);
const getDriverList = (limit, pageNo, params) => fetchData('GET', generateAPIUrlWithParams(API_URL.driverList, limit, pageNo, params));
const getDriverDetails = (driverId) => fetchData('GET', API_URL.driverDetails.replace('{driverId}', driverId));
const updateDriverDetails = (driverId, data) => fetchData('PUT', API_URL.updateDriver.replace('{driverId}', driverId), data);
// Approvers
const createApprover = (data) => fetchData('POST', API_URL.createApprover, data);
const getApproverList = (limit, pageNo, params) => fetchData('GET', generateAPIUrlWithParams(API_URL.approverList, limit, pageNo, params));
const getApproverDetails = (userId) => fetchData('GET', API_URL.approverDetails.replace('{userId}', userId));
const updateApproverDetails = (userId, data) => fetchData('PUT', API_URL.updateApprover.replace('{userId}', userId), data);
// site manager
const createSiteManager = (data) => fetchData('POST', API_URL.createSiteManager, data);
const getSiteManagerList = (limit, pageNo, params) => fetchData('GET', generateAPIUrlWithParams(API_URL.siteManagerList, limit, pageNo, params));
const getSiteManagerDetails = (userId) => fetchData('GET', API_URL.siteManagerDetails.replace('{userId}', userId));
const updateSiteManagerDetails = (userId, data) => fetchData('PUT', API_URL.updateSiteManager.replace('{userId}', userId), data);
// transactions
const createTransaction = (transactionType, data) => fetchData('POST', API_URL.createTransaction.replace('{transactionType}', transactionType), data);
const getTransactionList = (transactionType, limit, pageNo, params) => {
  const URL = API_URL.transactionList.replace('{transactionType}', transactionType);
  return fetchData('GET', generateAPIUrlWithParams(URL, limit, pageNo, params));
};
const updateOutwardOdometer = (transactionId, data) => fetchData(
  'PATCH',
  API_URL.updateOutwardOdometer(transactionId),
  data
);

const getTransactionDetails = (transactionType, transactionId) => fetchData('GET', API_URL.transactionDetails(transactionType, transactionId));
const updateTransactionDetails = (transactionType, transactionId, data) => fetchData('PUT', API_URL.updateTransaction(transactionType, transactionId), data);
const deleteTransaction = (transactionType, transactionId) => fetchData('DELETE', API_URL.deleteTransaction(transactionType, transactionId));
// dashboard
const getDashboardCount = () => fetchData('GET', API_URL.dashboardCount);
const getDashboardSales = (siteId = '') => fetchData('GET', API_URL.transactionChart.replace('{siteId}', siteId));
const getTerminalWiseFuelDispensed = (siteId = '') => fetchData('GET', API_URL.terminalWiseDispenseChart.replace('{siteId}', siteId));
const getTopFuelConsumption = (siteId = '') => fetchData('GET', API_URL.topFuelConsumptionChart.replace('{days}', siteId));
// Dropdown List
const getAllVehicles = () => fetchData('GET', API_URL.allVehicles);
const getAllOperators = () => fetchData('GET', API_URL.allOperators);
const getAllDrivers = () => fetchData('GET', API_URL.allDrivers);
const getAllSites = () => fetchData('GET', API_URL.allSites);
const getAllTanks = () => fetchData('GET', API_URL.allTanks);
const getAllSuppliers = () => fetchData('GET', API_URL.supplierList);
const getAllCards = () => fetchData('GET', API_URL.allCards);
const getAllRequestors = () => fetchData('GET', API_URL.allRequestors);
const getAllApprovers = () => fetchData('GET', API_URL.allApprovers);
const getAllSiteManagers = () => fetchData('GET', API_URL.allSiteManagers);
const getAllDispensers = () => fetchData('GET', API_URL.allDispensers);
// reports
const getMileageData = (limit, pageNo, params) => fetchData('GET', generateAPIUrlWithParams(API_URL.mileageReport, limit, pageNo, params));
const getCustomisedMileageData = (limit, pageNo, params) => fetchData('GET', generateAPIUrlWithParams(API_URL.customisedMileageReport, limit, pageNo, params));
const getReconciliationData = (limit, pageNo, params) => fetchData('GET', generateAPIUrlWithParams(API_URL.reconciliationReport, limit, pageNo, params));
const getLatestReconciliationData = (limit = 1000, pageNo = 1) => fetchData(
  'GET',
  generateAPIUrlWithParams(API_URL.reconciliationReport, limit, pageNo, { latest: true })
);
// card approval
const changeCardLimit = (data) => fetchData('POST', API_URL.changeCardLimit, data);
const getCardApprovalsList = (limit, pageNo, params) => fetchData('GET', generateAPIUrlWithParams(API_URL.cardLimitHistory, limit, pageNo, params));
const approveRejectCardLimit = (data) => fetchData('POST', API_URL.approveCardLimit, data);
const cardLimitApprovalsCount = () => fetchData('GET', API_URL.cardLimitRequestCount);
const cancelCardLimitRequest = (requestId, payload) => fetchData('PATCH', API_URL.cancelCardLimitRequest.replace('{requestId}', requestId), payload);
const getAllVehicleGroups = () => fetchData('GET', API_URL.getAllVehicleGroup);
const getAllDepartmentGroups = () => fetchData('GET', API_URL.getAllDepartmentGroup);
const getAllContractors = () => fetchData('GET', API_URL.getAllContractors);
const removeDispenserFromCard = (mapperId) => fetchData(
  'DELETE',
  API_URL.removeDispenserFromCard.replace('{mapperId}', mapperId)
);

// Wailon API's
const getVehicleFuelLevel = (vehicleId) => fetchData('GET', API_URL.getVehicleFuelLevel.replace('{vehicleID}', vehicleId));
const getWailonTransactions = (limit, pageNo, params) => fetchData('GET', generateAPIUrlWithParams(API_URL.getWailonTransactions, limit, pageNo, params));
const getVehicleSummary = (vehicleId) => fetchData('GET', API_URL.vehicleSummaryReport.replace('{vehicleID}', vehicleId));
const getVehicleUtilization = () => fetchData('GET', API_URL.vehicleUtilizationChart);
const getWailonVehicleList = () => fetchData('GET', API_URL.wailonVehicleList);
const getVehicleUtilizationReport = () => fetchData('GET', API_URL.vehicleUtilizationReport);
const getFleetCurrentState = () => fetchData('GET', API_URL.fleetCurrentState);
const getFleetAnalytics = (data) => fetchData('POST', API_URL.fleetAnalytics, data);
const getFleetStatistics = (data) => fetchData('POST', API_URL.fleetStatistics, data);
const OMNICOMM_STATISTICS_REQUEST_TIMEOUT_MS = 180000;
const getFleetStatisticsDashboard = (data) => fetchData(
  'POST',
  API_URL.fleetStatisticsDashboard,
  data,
  { timeout: OMNICOMM_STATISTICS_REQUEST_TIMEOUT_MS },
);
const getFleetTrack = (data) => fetchData('POST', API_URL.fleetTrack, data);
const getFleetTrackFuel = (data) => fetchData('POST', API_URL.fleetTrackFuel, data);
const getFleetFuelLevel = (data) => fetchData('POST', API_URL.fleetFuelLevel, data);
const getFleetTrackLastKnownPosition = (data) => fetchData('POST', API_URL.fleetTrackLastKnownPosition, data);
const getFleetLocationMapCard = (data) => fetchData('POST', API_URL.fleetLocationMapCard, data);
const getFleetVehicleProfile = (data) => fetchData('POST', API_URL.fleetVehicleProfile, data);
const getFleetFuelDraining = (data) => fetchData('POST', API_URL.fleetFuelDraining, data);
const getFleetTripReport = (data) => fetchData('POST', API_URL.fleetTripReport, data);
const getFleetCurrentRuns = (data) => fetchData('POST', API_URL.fleetCurrentRuns, data);
const getFleetEvents = (data) => fetchData('POST', API_URL.fleetEvents, data);
const getFleetGroupWork = (data) => fetchData('POST', API_URL.fleetGroupWork, data);
const getFleetLogReport = (data) => fetchData('POST', API_URL.fleetLogReport, data);
const getFleetDailyReadingSummary = (params) => fetchData('GET', generateAPIUrlWithQueryParams(API_URL.fleetDailyReadingSummary, params));
const getFleetGoogleMapsApiKey = () => fetchData('GET', API_URL.fleetGoogleMapsApiKey);
// vehicle breakdown
const getVehicleBreakDownList = (limit, pageNo, params) => fetchData('GET', generateAPIUrlWithParams(API_URL.breakDownList, limit, pageNo, params));
const createVehicleBreakDown = (data) => fetchData('POST', API_URL.createBreakDown, data);
const approveVehicleBreakDown = (data) => fetchData('POST', API_URL.approveBreakDown, data);
const getVehicleStatus = () => fetchData('GET', API_URL.vehicleStatus);
const createShift = (data) => fetchData('POST', API_URL.shift, data);
const updateShift = (shiftID, data) => fetchData('PUT', API_URL.updateShift.replace('{shiftID}', shiftID), data);
const getShiftDetails = () => fetchData('GET', API_URL.shift);
const getSettings = () => fetchData('GET', API_URL.settings);
const prepareDemoTransactions = () => fetchData('POST', API_URL.prepareDemoTransactions);
const getOtherReportsData = (url) => fetchData('GET', url);
// vehicle records
const getVehicleRecordList = (limit, pageNo, params) => fetchData('GET', generateAPIUrlWithParams(API_URL.vehicleRecordList, limit, pageNo, params));
const createVehicleRecord = (data) => fetchData('POST', API_URL.createVehicleRecord, data);
// notifications
const getNotificationsList = (limit, pageNo, params) => fetchData('GET', generateAPIUrlWithParams(API_URL.notifications, limit, pageNo, params));
const getNotificationDetails = (notificationId) => fetchData('GET', API_URL.notificationDetails.replace('{notificationId}', notificationId));
const getUnreadNotificationsCount = () => fetchData('GET', API_URL.unreadNotificationsCount);
const markNotificationRead = (notificationId) => fetchData('POST', API_URL.markNotificationRead.replace('{notificationId}', notificationId));
const markAllNotificationsRead = () => fetchData('POST', API_URL.markAllNotificationsRead);
// Tyres
const createTyre = (data) => fetchData('POST', API_URL.createTyre, data);
const getTyreList = (limit, pageNo, params) => fetchData('GET', generateAPIUrlWithParams(API_URL.tyreList, limit, pageNo, params));
const getTyreDetails = (id) => fetchData('GET', API_URL.tyreDetails.replace('{tyreId}', id));
const updateTyreDetails = (id, data) => fetchData('PUT', API_URL.updateTyreDetails.replace('{tyreId}', id), data);

export const APIService = {
  userLogin,
  userLogout,
  resetPassword,
  getTableColumnPreference,
  updateTableColumnPreference,
  getSupportMe,
  getSupportLookups,
  getSupportSummary,
  getSupportSites,
  createSupportSite,
  getSupportSiteDetails,
  updateSupportSite,
  getSupportIssueTransactions,
  exportSupportIssueTransactions,
  updateSupportIssueTransaction,
  getSupportInwardTransactions,
  createSupportInwardTransaction,
  updateSupportInwardTransaction,
  exportSupportInwardTransactions,
  getSupportTransferTransactions,
  exportSupportTransferTransactions,
  getSupportExternalTransactions,
  exportSupportExternalTransactions,
  getSupportVehicles,
  exportSupportVehicles,
  createSupportVehicle,
  getSupportVehicleDetails,
  updateSupportVehicle,
  getSupportTanks,
  createSupportTank,
  getSupportTankDetails,
  updateSupportTank,
  getSupportDispensers,
  createSupportDispenser,
  getSupportDispenserDetails,
  updateSupportDispenser,
  getSupportDispenserLinkedCards,
  resetSupportDispenserCards,
  mapSupportCardsToDispenser,
  getSupportCards,
  createSupportCard,
  getSupportCardDetails,
  updateSupportCard,
  exportSupportCards,
  removeSupportCardDispenser,
  getSupportMissedTransactions,
  createSite,
  getSiteList,
  getSiteDetails,
  updateSiteDetails,
  createRequestor,
  getRequestorList,
  getRequestorDetails,
  updateRequestorDetails,
  createVehicle,
  getVehicleList,
  getVehicleDetails,
  updateVehicleDetails,
  createGroup,
  getGroupList,
  updateGroupDetails,
  deleteGroup,
  createContractor,
  getContractorList,
  getContractorDetails,
  updateContractorDetails,
  patchContractorDetails,
  deleteContractor,
  getOperatorList,
  getOperatorDetails,
  updateOperatorDetails,
  createOperator,
  createTank,
  getTankList,
  getTankDetails,
  getTankFuelGraph,
  getTankFuelEvents,
  updateTankDetails,
  getDispenserLinkedCards,
  createDispenser,
  getDispenserList,
  getDispenserDetails,
  updateDispenserDetails,
  createCard,
  getCardList,
  getCardDetails,
  updateCardDetails,
  createDriver,
  getDriverList,
  getDriverDetails,
  updateDriverDetails,
  createApprover,
  getApproverList,
  getApproverDetails,
  updateApproverDetails,
  createSiteManager,
  getSiteManagerList,
  getSiteManagerDetails,
  updateSiteManagerDetails,
  createTransaction,
  getTransactionList,
  updateTransactionDetails,
  deleteTransaction,
  getTransactionDetails,
  getDashboardCount,
  getDashboardSales,
  getAllVehicles,
  getAllOperators,
  getAllDrivers,
  getAllSites,
  getAllTanks,
  getAllSuppliers,
  getAllCards,
  getAllRequestors,
  getAllApprovers,
  getAllSiteManagers,
  getAllDispensers,
  resetDispenserCards,
  getMileageData,
  getCustomisedMileageData,
  getReconciliationData,
  getLatestReconciliationData,
  changeCardLimit,
  getCardApprovalsList,
  approveRejectCardLimit,
  removeDispenserFromCard,
  cardLimitApprovalsCount,
  cancelCardLimitRequest,
  getAllVehicleGroups,
  getAllDepartmentGroups,
  getAllContractors,
  getVehicleFuelLevel,
  getTerminalWiseFuelDispensed,
  getTopFuelConsumption,
  getVehicleBreakDownList,
  createVehicleBreakDown,
  approveVehicleBreakDown,
  getWailonTransactions,
  getVehicleStatus,
  getVehicleUtilization,
  getVehicleSummary,
  getWailonVehicleList,
  getVehicleUtilizationReport,
  getFleetCurrentState,
  getFleetAnalytics,
  getFleetStatistics,
  getFleetStatisticsDashboard,
  getFleetTrack,
  getFleetTrackFuel,
  getFleetFuelLevel,
  getFleetTrackLastKnownPosition,
  getFleetLocationMapCard,
  getFleetVehicleProfile,
  getFleetFuelDraining,
  getFleetTripReport,
  getFleetCurrentRuns,
  getFleetEvents,
  getFleetGroupWork,
  getFleetLogReport,
  getFleetDailyReadingSummary,
  getFleetGoogleMapsApiKey,
  createShift,
  updateShift,
  getSettings,
  prepareDemoTransactions,
  getShiftDetails,
  getOtherReportsData,
  getVehicleRecordList,
  createVehicleRecord,
  getNotificationsList,
  getNotificationDetails,
  getUnreadNotificationsCount,
  markNotificationRead,
  markAllNotificationsRead,
  getTyreList,
  getTyreDetails,
  createTyre,
  updateTyreDetails,
  mapCardsToDispenser,
  updateOutwardOdometer
};
